<template>
  <div class="autocomplete">
    <input
      :id="id"
      v-model="searchTerm"
      maxlength="100"
      type="search"
      autocomplete="off"
      :placeholder="placeholderText"
      @focus="onChange"
      @input="onChange"
      @keydown.down="onArrowDown"
      @keydown.up="onArrowUp"
      @keydown.enter.prevent="onEnter"
      @keydown.esc.prevent="reset"
      @keydown.tab="reset"
    />
    <icon icon="angle-down" title="Zoeken" />
    <ul v-show="isOpen" id="autocomplete-results">
      <template v-for="result in results">
        <li v-if="result.options.length > 0" :key="result.name.slug">
          <strong>{{ result.name }}</strong>
        </li>

        <li
          v-for="option in result.options"
          :key="option.value"
          :class="{ 'is-active': option === activeResult, option: true }"
          @click="selectResult(option)"
        >
          {{ option.text }}
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'

export default {
  components: {
    Icon,
  },
  props: {
    optionGroups: {
      type: Array,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    modelValue: {
      type: String,
      default: '',
    },
    minChars: {
      type: Number,
      default: 0,
    },
    placeholderText: {
      type: String,
      default: '',
    },
  },
  emits: ['change'],
  data() {
    return {
      results: [],
      searchTerm: '',
      arrowCounter: 0,
    }
  },
  computed: {
    isOpen() {
      return this.results.length > 0
    },
    activeResult() {
      return this.getFlatResultsArray()[this.arrowCounter]
    },
  },

  watch: {
    modelValue() {
      this.searchTerm = this.modelValue
    },
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    this.searchTerm = this.modelValue
  },
  unmounted() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    onChange() {
      if (this.searchTerm.length >= this.minChars) {
        this.results = this.getFilteredOptionGroupResults(this.searchTerm)
      } else {
        this.reset()
      }
    },
    onArrowDown() {
      if (this.arrowCounter < this.getFlatResultsArray().length - 1) {
        this.arrowCounter += 1
      } else {
        this.arrowCounter = 0
      }
    },
    onArrowUp() {
      if (this.arrowCounter > 0) {
        this.arrowCounter -= 1
      } else {
        this.arrowCounter = this.getFlatResultsArray().length - 1
      }
    },
    onEnter() {
      const result = this.getFlatResultsArray()[this.arrowCounter]
      if (result) this.selectResult(result)
    },
    getFilteredOptionGroupResults(searchTerm) {
      const searchQuery = searchTerm.toLowerCase()
      return this.optionGroups.map((optionGroup) => {
        return {
          name: optionGroup.name,
          options: optionGroup.options.filter((option) =>
            option.text.toLowerCase().includes(searchQuery)
          ),
        }
      })
    },
    selectResult(result) {
      this.searchTerm = result.text
      this.$emit('change', result)
      this.reset()
    },
    reset() {
      this.results = []
      this.arrowCounter = -1
    },
    handleClickOutside(evt) {
      if (!this.$el.contains(evt.target)) {
        this.reset()
      }
    },
    /*
      map all the options in the optionGroups to a single array
    */
    getFlatResultsArray() {
      let newArray = []
      this.results.forEach((a) => (newArray = newArray.concat(a.options)))
      return newArray
    },
  },
}
</script>

<style lang="scss" scoped>
input {
  width: 100%;
}

.autocomplete {
  margin-bottom: 1em;
  position: relative;

  &:focus-within {
    input {
      border-color: $gray;
    }
    svg {
      color: $gray;
    }
  }
}

svg {
  position: absolute;
  right: 0.5em;
  top: 50%;
  margin-top: -12px;
  color: $color-border;
  transition: color 0.2s ease-in-out;
}

ul {
  @include list-reset();

  border: 2px solid $gray;
  position: absolute;
  background: $white;
  top: 100%;
  left: 0;
  z-index: z-index(autocomplete);

  min-width: 100%;
  max-height: 24em;
  overflow-y: scroll;
}

li {
  padding: 0.25em 0.625rem;
  cursor: pointer;

  white-space: nowrap;

  &.is-active,
  &:hover {
    background-color: $color-primary;
    color: $white;
  }

  &.option {
    text-indent: 1em;
  }
}
</style>
